import { useEffect, useMemo, useRef, useState } from "react";
import {
  ColumnDef,
  FilterFn,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button, Form, Table } from "react-bootstrap";

import FeatureFlag from "./FeatureFlag";
import FeatureFlagListPagination from "./FeatureFlagListPagination";
import FeatureFlagListToolbar from "./FeatureFlagListToolbar";
import FormatFeatureFlagDate from "./FormatFeatureFlagDate";

/** Save/delete failure info for one row. */
export interface FeatureFlagRowError {
  message: string;
  /** True when the flag never existed on the server (a new item that failed to save). */
  isNew: boolean;
}

interface FeatureFlagListProps {
  featureFlags: FeatureFlag[];
  /** Confirm-pending enabled value for one flag; the list does not remap the rows. */
  pendingToggle?: { id: number; enabled: boolean } | null;
  /** Rows to highlight as errored, keyed by flag id. */
  rowErrors: Map<number, FeatureFlagRowError>;
  /** Flag whose details panel is open; its row is highlighted. */
  selectedFlagId: number | null;
  /** Called when a row is clicked (outside its interactive controls). */
  onSelectFlag: (id: number) => void;
  onToggleEnabled: (id: number, enabled: boolean) => void;
  /** Reverts an existing errored row to its last saved values. */
  onResetFlag: (id: number) => void;
  /** Discards a new errored row that was never saved. */
  onRemoveFlag: (id: number) => void;
  /**
   * Starts the confirm-then-DELETE flow removing the flag from the selected
   * tenant: its ConditionalFeatureFlagTenant row (when present) and all of
   * the tenant's facility rows. The list is tenant-scoped, so the row's
   * remove control means "remove for this tenant", not a global flag delete.
   */
  onDeleteFlagForTenant: (id: number) => void;
}

const nameOrDescriptionFilter: FilterFn<FeatureFlag> = (row, _columnId, filterValue) => {
  const query = String(filterValue ?? "").trim().toLowerCase();
  if (!query) {
    return true;
  }

  const name = String(row.original.name ?? "").toLowerCase();
  const description = String(row.original.description ?? "").toLowerCase();
  return name.includes(query) || description.includes(query);
};

/**
 * Returns true when the click landed on an interactive control (switch,
 * button, link) whose own handler should win over row selection.
 */
function isInteractiveClick(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("button, input, label, a, select") !== null
  );
}

/** Feature flag list table with search, sort, and pagination. */
function FeatureFlagList({
  featureFlags,
  pendingToggle = null,
  rowErrors,
  selectedFlagId,
  onSelectFlag,
  onToggleEnabled,
  onResetFlag,
  onRemoveFlag,
  onDeleteFlagForTenant,
}: FeatureFlagListProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const pendingToggleRef = useRef(pendingToggle);
  pendingToggleRef.current = pendingToggle;

  const columns = useMemo<ColumnDef<FeatureFlag>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        enableGlobalFilter: true,
        cell: ({ getValue }) => (
          <span className="truncatedName" title={String(getValue() ?? "")}>
            {String(getValue() ?? "")}
          </span>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        enableSorting: false,
        enableGlobalFilter: true,
        cell: ({ getValue }) => (
          <span className="truncatedDescription" title={String(getValue() ?? "")}>
            {String(getValue() ?? "")}
          </span>
        ),
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        enableSorting: true,
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB, columnId) => {
          const rowADate = rowA.getValue<Date | null>(columnId);
          const rowBDate = rowB.getValue<Date | null>(columnId);
          const rowATimestamp = rowADate ? rowADate.getTime() : Number.NEGATIVE_INFINITY;
          const rowBTimestamp = rowBDate ? rowBDate.getTime() : Number.NEGATIVE_INFINITY;
          return rowATimestamp === rowBTimestamp ? 0 : rowATimestamp > rowBTimestamp ? 1 : -1;
        },
        cell: ({ getValue }) => FormatFeatureFlagDate(getValue() as Date | null),
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        enableSorting: true,
        enableGlobalFilter: false,
        sortingFn: (rowA, rowB, columnId) => {
          const rowADate = rowA.getValue<Date | null>(columnId);
          const rowBDate = rowB.getValue<Date | null>(columnId);
          const rowATimestamp = rowADate ? rowADate.getTime() : Number.NEGATIVE_INFINITY;
          const rowBTimestamp = rowBDate ? rowBDate.getTime() : Number.NEGATIVE_INFINITY;
          return rowATimestamp === rowBTimestamp ? 0 : rowATimestamp > rowBTimestamp ? 1 : -1;
        },
        cell: ({ getValue }) => FormatFeatureFlagDate(getValue() as Date | null),
      },
      {
        accessorKey: "enabled",
        header: "Enabled",
        enableSorting: true,
        enableGlobalFilter: false,
        cell: ({ row }) => {
          const error = rowErrors.get(row.original.id);

          return (
            <div className="rowActions">
              <div className="rowActionButtons">
                <Form.Check
                  type="switch"
                  id={`feature-flag-enabled-${row.original.id}`}
                  className="mb-0 enabledSwitch"
                  checked={
                    pendingToggleRef.current?.id === row.original.id
                      ? pendingToggleRef.current.enabled
                      : row.original.enabled
                  }
                  disabled={row.original.conditionalFeatureFlagTenantId == null}
                  onChange={(e) => onToggleEnabled(row.original.id, e.target.checked)}
                  aria-label={`Enabled status for ${row.original.name}`}
                />
                <Button
                  variant="link"
                  size="sm"
                  type="button"
                  className="text-danger deleteButton"
                  aria-label={`Remove ${row.original.name} from ${row.original.tenantName}`}
                  onClick={() => onDeleteFlagForTenant(row.original.id)}
                >
                  <i className="fa fa-trash" />
                </Button>
              </div>
              {error && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  type="button"
                  className="errorActionButton"
                  title={error.message}
                  onClick={() =>
                    error.isNew ? onRemoveFlag(row.original.id) : onResetFlag(row.original.id)
                  }
                >
                  {error.isNew ? "Remove" : "Reset"}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [onToggleEnabled, rowErrors, onResetFlag, onRemoveFlag, onDeleteFlagForTenant]
  );

  const table = useReactTable({
    data: featureFlags,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: nameOrDescriptionFilter,
    // Keep the current page when flags change (toggles, saves, reloads);
    // search resets the page itself and the effect below clamps overflow.
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  // When rows disappear (deletes), the current page can fall past the end;
  // snap back to the new last page.
  const pageCount = table.getPageCount();
  useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex > pageCount - 1) {
      setPagination((current) => ({ ...current, pageIndex: pageCount - 1 }));
    }
  }, [pageCount, pagination.pageIndex]);

  return (
    <div className="featureFlagList">
      <FeatureFlagListToolbar
        pageSize={pagination.pageSize}
        searchValue={globalFilter}
        onPageSizeChange={(pageSize) =>
          setPagination({ pageIndex: 0, pageSize })
        }
        onSearchChange={(value) => {
          setGlobalFilter(value);
          setPagination((current) => ({ ...current, pageIndex: 0 }));
        }}
      />

      <Table className="featureFlagTable table-striped mb-0">
        <colgroup>
          <col className="flagNameCol" />
          <col className="flagDescriptionCol" />
          <col className="flagDateCol" />
          <col className="flagDateCol" />
          <col className="flagEnabledCol" />
        </colgroup>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                return (
                  <th
                    key={header.id}
                    className={canSort ? "sortableHeader" : undefined}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    aria-sort={
                      sorted === "asc"
                        ? "ascending"
                        : sorted === "desc"
                          ? "descending"
                          : canSort
                            ? "none"
                            : undefined
                    }
                  >
                    <span className="headerContent">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <span className="sortIcons" aria-hidden="true">
                          <i className={`fa fa-caret-up${sorted === "asc" ? " is-active" : ""}`} />
                          <i className={`fa fa-caret-down${sorted === "desc" ? " is-active" : ""}`} />
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="text-center text-muted py-4">
                No feature flags found.
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => {
              const classNames = ["selectableRow"];
              if (rowErrors.has(row.original.id)) {
                classNames.push("flagRowError");
              }
              if (row.original.id === selectedFlagId) {
                classNames.push("selectedRow");
              }

              return (
                <tr
                  key={row.id}
                  className={classNames.join(" ")}
                  onClick={(e) => {
                    if (!isInteractiveClick(e.target)) {
                      onSelectFlag(row.original.id);
                      e.stopPropagation();
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      <FeatureFlagListPagination
        pageIndex={pagination.pageIndex}
        pageCount={table.getPageCount()}
        pageSize={pagination.pageSize}
        filteredRowCount={filteredRowCount}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        onPreviousPage={() => table.previousPage()}
        onNextPage={() => table.nextPage()}
        onGoToPage={(page) => table.setPageIndex(page)}
      />
    </div>
  );
}

export default FeatureFlagList;
